import { useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { getWorkflowOrders, WorkflowOrder } from '../Api/Orders/ordersApi';
import { addError } from './Error';
import { getSelectedAccountIdsForUrl } from './User';
import { accountScopeKey } from '../utils/accountIds';

const subject = new BehaviorSubject<WorkflowOrder[]>([]);
const loadingSubject = new BehaviorSubject<boolean>(false);

export const getWorkflowOrdersSubject = (): BehaviorSubject<WorkflowOrder[]> => subject;
export const getWorkflowOrdersLoadingSubject = (): BehaviorSubject<boolean> => loadingSubject;

export interface UseWorkflowOrdersReturn {
  orders: WorkflowOrder[];
  loading: boolean;
  fetchWorkflowOrders: (forceRefresh?: boolean) => Promise<void>;
  clearWorkflowOrders: () => void;
}

// Held rather than read off loadingSubject: the spinnerless path leaves that flag alone, so it cannot serialise.
let ordersInFlight: Promise<void> | null = null;

// Not every scope change forces a read, so a list read under another scope must not pass for this one's.
let listScope = '';

// A read begun after a caller asked sees any write that caller settled before asking, so a queue of them needs only one more.
let readsBegun = 0;

export interface FetchWorkflowOrdersOptions {
  showSpinner?: boolean;
  // Opt-in, as a caller whose write is still in flight would be handed a list read before that write landed.
  afterSettledWrite?: boolean;
}

const readOrders = async (showSpinner: boolean): Promise<void> => {
  // A tag change refreshes one row's worth of data; flipping the shared loading state blanks the whole grid for it.
  if (showSpinner) loadingSubject.next(true);

  try {
    const accountIds = getSelectedAccountIdsForUrl();
    const { data, error } = await getWorkflowOrders(accountIds);
    // A switch while this ran emptied the list for the new scope, so the old scope's answer must not refill it.
    if (accountScopeKey(getSelectedAccountIdsForUrl()) !== accountScopeKey(accountIds)) return;
    if (data) {
      listScope = accountScopeKey(accountIds);
      subject.next(data);
      addError('workflow-orders', undefined);
    }
    if (error) {
      subject.next([]);
      addError('workflow-orders', error.message);
    }
  } finally {
    if (showSpinner) loadingSubject.next(false);
  }
};

// Module-level so the shared grid column defs, which have no hook in scope, can refresh the list.
export const fetchWorkflowOrders = async (
  forceRefresh = false,
  { showSpinner = true, afterSettledWrite = false }: FetchWorkflowOrdersOptions = {}
): Promise<void> => {
  const readsBeforeAsking = readsBegun;
  // Queued, not dropped, so a tag write still lands; and behind every read in flight, as overlapping reads can answer out of order.
  while (ordersInFlight) await ordersInFlight;
  const accountIds = getSelectedAccountIdsForUrl();
  const fresh = !forceRefresh || (afterSettledWrite && readsBegun > readsBeforeAsking);
  if (subject.getValue().length > 0 && fresh && listScope === accountScopeKey(accountIds)) return;
  // No account scope yet: the read refuses, and the error branch would file that refusal as a failed load.
  if (accountIds.length === 0) return;

  readsBegun += 1;
  const request = readOrders(showSpinner);
  ordersInFlight = request;
  try {
    await request;
  } finally {
    if (ordersInFlight === request) ordersInFlight = null;
  }
};

export const useWorkflowOrders = (): UseWorkflowOrdersReturn => {
  const [orders, setOrders] = useState<WorkflowOrder[]>(subject.getValue());
  const [loading, setLoading] = useState<boolean>(loadingSubject.getValue());

  useEffect(() => {
    const subSub = subject.subscribe(setOrders);
    const loadingSub = loadingSubject.subscribe(setLoading);
    return () => {
      subSub.unsubscribe();
      loadingSub.unsubscribe();
    };
  }, []);

  const clearWorkflowOrders = () => subject.next([]);

  return { orders, loading, fetchWorkflowOrders, clearWorkflowOrders };
};
